import {
  Avatar,
  Stack,
  Typography,
  Skeleton,
  Badge,
  Chip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { setSelectedChat } from "../../lib/slices/selectedChatSlice";
import { useDispatch, useSelector } from "react-redux";
import { getConversationByCompanyId } from "../../services/common/chatApiFn";
import moment from "moment";
import { t } from "i18next";

function AdminChat({ socket }) {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.userData.user);
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (userData) {
      fetchAdminConversation();
    }
  }, [userData]);

  useEffect(() => {
    if (socket) {
      socket.emit("connectWithAdmin", { room: "adminRoom" });

      const handleNewConversation = (newConversation) => {
        if (newConversation.conversationId === conversation?.conversationId) {
          setConversation((prev) => ({
            ...prev,
            lastMessage: newConversation.lastMessage,
            lastUpdated: new Date(),
            unreadMessagesCount: newConversation.unreadMessagesCount,
          }));
        }
      };

      socket.on("newConversation", handleNewConversation);

      return () => {
        socket.off("newConversation", handleNewConversation);
        socket.emit("leaveRoom", { room: "adminRoom" });
      };
    }
  }, [socket, conversation]);

  const fetchAdminConversation = async () => {
    try {
      const response = await getConversationByCompanyId(userData?.adminId);
      if (response.data.status === "success") {
        const data = response.data.data;
        let adminData = null;
        let companyData = null;

        data.participants.forEach((val) => {
          if (val.role === "Admin") {
            adminData = val.userId;
          } else {
            companyData = val.userId;
          }
        });

        setConversation({
          ...data,
          user: {
            Admin: adminData,
            Company: companyData,
          },
        });
        setLoading(false);
      }
    } catch (error) {
      setError(true);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Stack
        p={"6px 6px"}
        borderRadius={2}
        border={"1px solid #615EF0"}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        sx={{ cursor: "default" }}
      >
        <Skeleton variant="circular" width={48} height={48} />
        <Stack flex={1} ml={2}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="80%" />
        </Stack>
      </Stack>
    );
  }

  if (error || !conversation) {
    return (
      <Stack
        p={"6px 6px"}
        borderRadius={2}
        border={"1px solid #615EF0"}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        sx={{ cursor: "default" }}
      >
        <Typography
          fontSize={"0.875rem"}
          fontWeight={"bold"}
          color="error"
          width="100%"
          textAlign="center"
        >
          {t("somethingWentWrong")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      p={"6px 6px"}
      borderRadius={2}
      border={"1px solid #615EF0"}
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"flex-start"}
      sx={{ cursor: "pointer" }}
      onClick={() =>
        dispatch(
          setSelectedChat({
            _id: conversation?._id,
            name: "Admin",
            receiverId: conversation?.user?.Admin?._id,
            image: conversation?.user?.Admin?.profileImg,
            conversationId: conversation?.conversationId,
            senderRole: "Company",
            receiverRole: "Admin",
            type: "AdminWithCompany",
          })
        )
      }
    >
      <Stack
        gap={1}
        direction={"row"}
        width={"100%"}
        alignItems={"center"}
        overflow={"hidden"}
      >
        <Avatar
          src={conversation?.user?.Admin?.profileImg}
          width={48}
          height={48}
        />

        <Stack overflow={"hidden"} width={"100%"}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Typography
              variant="body1"
              // fontSize={"0.875rem"}
              fontWeight={"bold"}
              noWrap
            >
              {t("chatWithAdmin")}
            </Typography>
            <Typography fontSize={"0.75rem"}>
              {moment(conversation?.lastUpdated).format("hh:mm a")}
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Typography variant="body2" color={"#7c7c7c"} noWrap>
              {conversation?.lastMessage || t("welcomeChatMessage")}
            </Typography>
            {conversation?.unreadMessagesCount[userData?.adminId] > 0 && (
              <Chip
                label={
                  conversation?.unreadMessagesCount[userData?.adminId] || 0
                }
                sx={{
                  color: "white",
                  height: "auto",
                  backgroundColor: "primary.main",
                  "& .MuiChip-label": {
                    padding: "2px 8px 2px 8px",
                  },
                }}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default AdminChat;
